import * as THREE from 'three';
import { CadObject } from '../../../models/cad-object.model';

const DEFAULT_HOVER_COLOR = new THREE.Color('Orange');

export interface HoverHighlightState {
  raycaster: THREE.Raycaster;
  mouseNdc: THREE.Vector2;
  currentHoveredCadObject: CadObject | null;
  currentHoveredOriginalColor: THREE.Color | null;
}

export function createHoverHighlightState(): HoverHighlightState {
  return {
    raycaster: new THREE.Raycaster(),
    mouseNdc: new THREE.Vector2(),
    currentHoveredCadObject: null,
    currentHoveredOriginalColor: null,
  };
}

export function handleHoverHighlight(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.PerspectiveCamera,
  objectGroup: THREE.Group,
  cadObjects: CadObject[],
  state: HoverHighlightState,
): void {
  const rect = canvas.getBoundingClientRect();
  state.mouseNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  state.mouseNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  state.raycaster.setFromCamera(state.mouseNdc, camera);
  const intersects = state.raycaster.intersectObjects(
    objectGroup.children,
    true,
  );

  setHoverHighlight(findHoveredCadObject(intersects, cadObjects), state);
}

export function clearHoverHighlight(state: HoverHighlightState): void {
  if (
    !state.currentHoveredCadObject?.mesh ||
    !state.currentHoveredOriginalColor
  )
    return;

  const material = state.currentHoveredCadObject.mesh
    .material as THREE.MeshStandardMaterial;
  material.color.copy(state.currentHoveredOriginalColor);
  material.needsUpdate = true;
  state.currentHoveredCadObject = null;
  state.currentHoveredOriginalColor = null;
}

/**
 * Applies the standard hover treatment to a known CAD object, such as one
 * hovered in an adjacent object tree, without requiring a canvas raycast.
 */
export function setHoverHighlight(
  cadObject: CadObject | null,
  state: HoverHighlightState,
): void {
  if (cadObject === state.currentHoveredCadObject) return;

  clearHoverHighlight(state);
  if (cadObject) {
    applyHoverHighlight(cadObject, state);
  }
}

function findHoveredCadObject(
  intersects: THREE.Intersection[],
  cadObjects: CadObject[],
): CadObject | null {
  const hoveredObject = intersects.find(
    (intersect) =>
      intersect.object instanceof THREE.Mesh &&
      intersect.object.visible &&
      intersect.object.userData['overlayType'] === undefined,
  );

  if (!hoveredObject) return null;

  return cadObjects.find((obj) => obj.mesh === hoveredObject.object) ?? null;
}

function applyHoverHighlight(
  cadObject: CadObject,
  state: HoverHighlightState,
): void {
  if (!cadObject.mesh) return;
  const material = cadObject.mesh.material as THREE.MeshStandardMaterial;
  state.currentHoveredOriginalColor = material.color.clone();
  material.color.copy(DEFAULT_HOVER_COLOR);
  material.needsUpdate = true;
  state.currentHoveredCadObject = cadObject;
}
